/*
 * Migrates old negative number temporary shipment ids to ClientIds
 */

import type { ClientId } from '../../core/ClientId';
import { generateClientId } from '../../core/ClientId';
import { isCoaEntry, isSpotEntry } from '../../core/Core';
import type { InternalUserSchedule } from '../../Scenario';
import { getOrderAssignments } from '@seaber/common/core/scenario';
import type { MigrateData, ScenarioMigration } from './migrate';
import { DefaultMap } from '@seaber/common/lib/collections';

const migrateTmpShipmentIds: ScenarioMigration = (
  scenario: InternalUserSchedule,
  _migrateData: MigrateData,
): InternalUserSchedule => {
  if (scenario.type !== 'user' || scenario.patchOrders.size < 1) {
    // No need to migrate backups or master scenario as those don't contain
    // order patches
    return scenario;
  }

  const assignments = getOrderAssignments(scenario);

  const clientIdByNumericIdByTimelineKey = new DefaultMap<
    number,
    Map<string, ClientId>
  >(() => new Map());

  const patchArr = Array.from(scenario.patchOrders.entries());

  const migratedPatches: typeof scenario.patchOrders = new Map(
    patchArr.map(([orderId, patch]) => {
      const oldShipmentId = patch.patch.shipmentId;
      const assignment = assignments.get(orderId);

      if (typeof oldShipmentId !== 'number' || oldShipmentId >= 0) {
        // No need to migrate this patch
        return [orderId, patch];
      }

      if (!assignment) {
        console.error(
          'Error migrating shipment ids, unable to find assignment for order',
          orderId,
        );
      }

      // Key assignments by contract type, contract id and vessel
      // to match how users expect assignments to be grouped on timelines
      const typeKey = assignment?.category ?? 'unknown';
      const contractKey = assignment
        ? isCoaEntry(assignment)
          ? assignment.coaId
          : isSpotEntry(assignment)
            ? assignment.ownerOrganisationId
            : 'tc'
        : 'unknown';
      const vesselKey = assignment?.vesselId ?? 'unknown';

      const timelineKey = `${typeKey}-${contractKey}-${vesselKey}`;

      const existingNewId = clientIdByNumericIdByTimelineKey
        .get(oldShipmentId)
        .get(timelineKey);

      const newId = existingNewId ? existingNewId : generateClientId();

      if (!existingNewId) {
        clientIdByNumericIdByTimelineKey
          .get(oldShipmentId)
          .set(timelineKey, newId);
      }

      const migratedPatch = {
        ...patch,
        patch: {
          ...patch.patch,
          shipmentId: newId,
        },
      };

      return [orderId, migratedPatch];
    }),
  );

  return {
    ...scenario,
    patchOrders: migratedPatches,
  };
};

migrateTmpShipmentIds.migrateAlwaysIf = (scenario) =>
  [...scenario.patchOrders.values()].some(
    (patch) =>
      typeof patch.patch.shipmentId === 'number' && patch.patch.shipmentId < 0,
  );

export default migrateTmpShipmentIds;
